import { LocaleType } from '../../constants/localeConstants';
import {
  DateDisplayFormat,
  DateTimeEvaluationMode,
  ExtractedInfoByMode,
  InkDateType,
  LocaleMapping,
} from '../../types';
import { getResolvedLocale } from '../internal/getResolvedLocales';
import { normalizeDate } from '../normalizeDate/normalizeDate';
import { normalizeDateTime } from '../normalizeDateTime/normalizeDateTime';
import { normalizeTime } from '../normalizeTime/normalizeTime';

import { START_OF_DAY_TIME_PARTS } from '../../constants/timeConstants';
import { formatNativeDate } from '../formatDateTime/formatNativeDate';
import { createInstantGenerators } from '../internal/temporalDate/createInstantGenerator';
import { createDateFromInstant } from '../internal/temporalDate/createDateFromInstant';
import { getTimezone } from './helpers/getTimezone';

type extractDateInfoArgs<M extends DateTimeEvaluationMode> = {
  value: InkDateType;
  locale?: LocaleType | LocaleMapping;
  format?: DateDisplayFormat;
  evaluationMode?: M;
  timezone?: string;
};

export function extractDateTimeInfo<M extends DateTimeEvaluationMode>(
  args: extractDateInfoArgs<M>,
): ExtractedInfoByMode<M> {
  const { value, locale, format = 'condensed', evaluationMode = 'date', timezone } = args;
  const locales = getResolvedLocale(locale);
  const baseResult = {
    input: value,
    inputLocale: locales.input,
    outputLocale: locales.output,
  };
  const localTimezone = getTimezone('local');
  switch (evaluationMode) {
    case 'time': {
      const result = normalizeTime({ value, locale: locales.input });
      return {
        ...baseResult,
        mode: 'time',
        ...result,
      } as ExtractedInfoByMode<M>;
    }
    case 'date-time': {
      // eslint-disable-next-line prefer-const
      let result = normalizeDateTime({ value, locale: locales.input });
      if (result.hasOnlyDate) {
        result = { ...result, dateTimeParts: { ...result.dateTimeParts, ...START_OF_DAY_TIME_PARTS } };
      }
      const instantGenerator = createInstantGenerators(getTimezone(result.timeZoneSource));
      const instant = instantGenerator.withTime(result.dateTimeParts);
      const date = instant && createDateFromInstant(instant);
      return {
        ...baseResult,
        mode: 'date-time',
        ...result,
        formatted: {
          utc: date ? formatNativeDate(date, format, locales.output, 'UTC') : undefined,
          local: date ? formatNativeDate(date, format, locales.output, localTimezone) : undefined,
          timeZone: date && timezone ? formatNativeDate(date, format, locales.output, timezone) : undefined,
        },
        date,
      } as ExtractedInfoByMode<M>;
    }
    case 'date':
    default: {
      const result = normalizeDate({ value, locale: locales.input });
      const instantGenerator = createInstantGenerators(getTimezone(result.timeZoneSource));
      const instant = instantGenerator.withTime(result.dateParts);
      const date = instant && createDateFromInstant(instant);
      return {
        ...baseResult,
        mode: 'date',
        ...result,
        formatted: {
          utc: date ? formatNativeDate(date, format, locales.output, 'UTC') : undefined,
          local: date ? formatNativeDate(date, format, locales.output, localTimezone) : undefined,
          timeZone: date && timezone ? formatNativeDate(date, format, locales.output, timezone) : undefined,
        },
        date,
      } as ExtractedInfoByMode<M>;
    }
  }
}
